/**
 * Sites & Operators hooks — Phase 24 / Task 24.3 (DEC-094).
 *
 * TanStack Query hooks for the hauler catalog junctions:
 *   - hauler_operators (+ platform_operators catalog search)
 *   - tank_batteries (+ linked wells + tanks + per-battery audit)
 *   - hauler_disposal_facilities (+ wells catalog search)
 *
 * Reads gate on VIEW_HAULER_CATALOG (DISPATCHER+); writes on MANAGE_HAULER_CATALOG
 * (DISPATCH_MANAGER+); the hauler-operator delete on MANAGE_TENANT (TENANT_ADMIN).
 * The server is the authoritative gate — the nav is hidden for non-managers as UX
 * only, and the read tabs are visible to plain dispatchers.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery, useApiMutation, useApiOpts } from '@lodex-pro/web-shared';
import type { TankShape } from '@lodex-pro/shared/tank-strapping';
import { WELL_SEARCH_MAX_LIMIT } from '@lodex-pro/shared/wells-paging';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface HaulerOperator {
  readonly id: string;
  readonly operatorId: string | null;
  readonly isPlatformOperator: boolean;
  readonly customerName: string | null;
  readonly customerAbbreviation: string;
  readonly billingContactName: string | null;
  readonly billingContactEmail: string | null;
  readonly billingContactPhone: string | null;
  readonly billingAddress: string | null;
  readonly isActive: boolean;
  /** The OFFICIAL regulator name of a catalog operator (null for a private customer). */
  readonly catalogName: string | null;
  /** Paelode's hauler-facing name for a catalog operator, when set (0202). */
  readonly displayName: string | null;
  /**
   * Paelode's abbreviation for a catalog operator (head of its merge chain), null for a
   * private customer or a catalog operator with none yet. When set, tickets use it and the
   * hauler's own customerAbbreviation is not asked for (Corbin 2026-10-17).
   */
  readonly platformAbbreviation: string | null;
  /** The code this customer's tickets carry (the same rule HaulId issuance uses). */
  readonly ticketAbbreviation: string;
  readonly catalogState: string | null;
  readonly regulatorOperatorId: string | null;
  // Task 24.6 — platform read-through flags (null for hauler-private customers).
  readonly isProducer: boolean | null;
  readonly isDisposal: boolean | null;
  readonly resolvedName: string;
  /**
   * DEC-198 — per-customer pricing. These are the FIRST rate the biller looks at:
   * customer → platform operator → job-type default. null = fall through to the
   * next level, which is not the same as "free".
   */
  readonly perBblPriceCents: number | null;
  readonly hourlyPriceCents: number | null;
  readonly bidPriceCents: number | null;
  /**
   * DEC-192 option A, completed (migration 0086) — when the HOURLY meter starts and
   * stops for this customer. null = inherit the operator/tenant policy.
   */
  readonly billingStart: 'ARRIVAL' | 'START' | null;
  readonly billingEnd: 'DEPARTURE' | 'COMPLETION' | null;
}

export interface PlatformOperatorResult {
  readonly id: string;
  /** The official regulator name. */
  readonly name: string;
  /** Paelode's hauler-facing name, when set (0202). */
  readonly displayName: string | null;
  /** Paelode's abbreviation, when set: tickets for this operator use it. */
  readonly abbreviation: string | null;
  readonly state: string | null;
  readonly regulatorOperatorId: string | null;
  readonly isProducer: boolean;
  readonly isDisposal: boolean;
}

/** Task 24.6 — an active standalone (well-direct) tank, surfaced on well search. */
export interface StandaloneTank {
  readonly id: string;
  readonly name: string;
  readonly capacityBbl: string | null;
}

export interface WellResult {
  readonly id: string;
  readonly wellName: string;
  readonly apiNumber: string;
  readonly operatorId: string | null;
  readonly operatorName: string | null;
  readonly county: string | null;
  readonly state: string;
  readonly wellType: string | null;
  // Task 24.6 — non-null when this tenant has an active standalone tank on the well.
  readonly standaloneTank: StandaloneTank | null;
  // Task 24.11 — count of this tenant's active standalone tanks on the well (badge).
  readonly tankCount: number;
  // Task 24.11 (DEC-105) — true when the well is in this hauler's catalog
  // (hauler_wells); the search floats these to the top.
  readonly inHaulerCatalog: boolean;
}

export interface TankBatterySummary {
  readonly id: string;
  readonly name: string;
  readonly haulerOperatorId: string;
  readonly isPrivate: boolean;
  readonly isActive: boolean;
  readonly operatorName: string;
  readonly operatorAbbreviation: string;
  // Phase 26 / Task 26.5 (DEC-147 #3 / #5) — surfaces the tenant-visible
  // site type + per-location capability flags. `siteTypeCode` gates the
  // Wells section (only TANK_BATTERY has one); the toggle chips read
  // `canPickup` / `canDropoff`. Nullable when a location predates typing.
  readonly siteTypeId: string | null;
  readonly siteTypeCode: string | null;
  readonly siteTypeLabel: string | null;
  readonly canPickup: boolean;
  readonly canDropoff: boolean;
  // DEC-310 — USER = typed by a person, DERIVED = worked out from GPS / wells.
  readonly county: string | null;
  readonly countyState: string | null;
  readonly countySource: 'USER' | 'DERIVED' | null;
  readonly wellCount: number;
  readonly tankCount: number;
}

export interface LinkedWell {
  readonly linkId: string;
  readonly wellId: string;
  readonly wellName: string;
  readonly apiNumber: string;
  readonly county: string | null;
  readonly state: string;
  readonly wellType: string | null;
}

export interface CatalogTank {
  readonly id: string;
  readonly name: string;
  readonly capacityBbl: string | null;
  readonly bblPerInch: string | null;
  readonly tankType: string | null;
  readonly producerIdentifier: string | null;
  readonly standardTankCatalogId: string | null;
  /** Phase 13.4a (0236) — the haul threshold (% full); null = none. Older APIs omit it. */
  readonly thresholdPercent?: string | null;
  /** Hours ahead auto-haul acts on a predicted crossing (24 by default). */
  readonly leadTimeHours?: number;
  /** The job type an auto job gets; null = no auto job for this tank. */
  readonly autoHaulJobTypeFamilyId?: string | null;
  readonly isActive: boolean;
}

export interface TankBatteryDetail extends TankBatterySummary {
  readonly wells: readonly LinkedWell[];
  readonly tanks: readonly CatalogTank[];
  // Task 24.6 — geofence status for the detail badge (site_geofences keyed by
  // site_id = battery id, site_type='TANK_BATTERY').
  readonly geofenceExists?: boolean;
  readonly geofenceVerified?: boolean;
  /** Phase 13.4a (0236) — auto-haul switched on for this site (off by default). */
  readonly autoHaulEnabled?: boolean;
}

export interface StandardTank {
  readonly id: string;
  readonly name: string;
  readonly capacityBbl: string;
  readonly bblPerInch: string;
  // DEC-379 (0265): no tank type; the size, when known, labels the picker option.
  readonly shape?: TankShape;
  readonly diameterFt?: string | null;
  readonly lengthFt?: string | null;
  readonly widthFt?: string | null;
  readonly heightFt?: string | null;
  // Task 24.5 — /v1/platform/tank-catalog returns all rows incl. inactive; the
  // "Add Tank" dropdown filters to active.
  readonly isActive: boolean;
}

export interface AuditEntry {
  readonly id: string;
  readonly entityType: string;
  readonly action: string;
  readonly changedAt: string;
  readonly notes: string | null;
  readonly changedByName: string | null;
  readonly changedByRole: string | null;
}

export interface DisposalFacility {
  readonly id: string;
  readonly wellId: string;
  readonly displayName: string | null;
  readonly isActive: boolean;
  readonly wellName: string;
  readonly apiNumber: string;
  readonly wellType: string | null;
  readonly county: string | null;
  readonly state: string;
  readonly operatorName: string | null;
  readonly resolvedName: string;
}

// ─── Query keys ──────────────────────────────────────────────────────────────

export const HAULER_OPERATORS_KEY = ['hauler-operators'] as const;
export const TANK_BATTERIES_KEY = ['tank-batteries'] as const;
export const TANK_BATTERY_KEY = (id: string): readonly string[] => ['tank-batteries', id];
export const TANK_BATTERY_AUDIT_KEY = (id: string): readonly string[] => [
  'tank-batteries',
  id,
  'audit',
];
export const DISPOSAL_FACILITIES_KEY = ['hauler-disposal-facilities'] as const;
export const STANDARD_TANKS_KEY = ['platform', 'tank-catalog'] as const;
// Phase 26 / Task 26.5 (DEC-147 #3) — tenant-filtered site_types catalog. The
// picker on the Create Location form + the badge on the list rely on this.
export const SITE_TYPES_KEY = ['site-types'] as const;

// ─── Site types (Phase 26.5 / DEC-147 #3) ────────────────────────────────────

export interface SiteType {
  readonly id: string;
  readonly code: string;
  readonly label: string;
  readonly sortOrder: number;
}

export function useSiteTypes(): ReturnType<typeof useApiQuery<{ siteTypes: readonly SiteType[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ siteTypes: readonly SiteType[] }>(opts, '/v1/site-types', {
    queryKey: SITE_TYPES_KEY,
    // Platform-managed catalog + tenant overlay — refetch is rare, so 5 min is safe.
    staleTime: 5 * 60_000,
  });
}

// ─── Hauler operators ────────────────────────────────────────────────────────

export function useHaulerOperators(): ReturnType<
  typeof useApiQuery<{ haulerOperators: readonly HaulerOperator[] }>
> {
  const opts = useApiOpts();
  return useApiQuery<{ haulerOperators: readonly HaulerOperator[] }>(opts, '/v1/hauler-operators', {
    queryKey: HAULER_OPERATORS_KEY,
    staleTime: 30_000,
  });
}

/**
 * One customer (GET /v1/hauler-operators/:id). Keyed under HAULER_OPERATORS_KEY, so
 * the update mutation's invalidation refreshes it too. `null` = not loaded.
 */
export function useHaulerOperator(
  id: string | null,
): ReturnType<typeof useApiQuery<{ haulerOperator: HaulerOperator }>> {
  const opts = useApiOpts();
  return useApiQuery<{ haulerOperator: HaulerOperator }>(opts, `/v1/hauler-operators/${id ?? ''}`, {
    queryKey: [...HAULER_OPERATORS_KEY, id ?? '__none__'],
    enabled: id !== null,
    staleTime: 0,
  });
}

// ─── Customer job-type prices, read-only (gap 1 / DEC-383, D-10) ─────────────
// Edited in web-billing (Billing → Customer prices); dispatch only reads them. Only the fields this
// app shows are typed; the API's CustomerJobTypePricesResponse carries more (special, scheduled).

export type JobTypePriceMethod = 'PER_BBL' | 'HOURLY' | 'PER_LOAD';

export interface JobTypePriceViewRow {
  readonly familyId: string;
  readonly label: string;
  readonly billingMethod: JobTypePriceMethod;
  /** The price today and where it came from; null = "No price". */
  readonly today: {
    readonly rateCents: number;
    readonly source: { readonly kind: 'CUSTOMER_JOB_TYPE' | 'CUSTOMER' | 'JOB_TYPE' };
  } | null;
  /** D-04: this customer's price is in another unit than the job type bills today. */
  readonly otherUnit: {
    readonly rateCents: number;
    readonly billingMethod: JobTypePriceMethod;
    readonly effectiveFrom: string;
  } | null;
}

export interface CustomerJobTypePricesView {
  readonly customer: { readonly id: string; readonly name: string };
  readonly today: string;
  readonly rows: readonly JobTypePriceViewRow[];
}

export const CUSTOMER_JOB_TYPE_PRICES_VIEW_KEY = ['customer-job-type-prices-view'] as const;

export function useCustomerJobTypePricesView(
  haulerOperatorId: string | null,
): ReturnType<typeof useApiQuery<CustomerJobTypePricesView>> {
  const opts = useApiOpts();
  return useApiQuery<CustomerJobTypePricesView>(
    opts,
    `/v1/customer-prices/${haulerOperatorId ?? ''}/job-types/dispatch-view`,
    {
      queryKey: [...CUSTOMER_JOB_TYPE_PRICES_VIEW_KEY, haulerOperatorId ?? '__none__'],
      enabled: haulerOperatorId !== null,
      staleTime: 30_000,
    },
  );
}

export interface CreateHaulerOperatorBody {
  operatorId?: string;
  customerName?: string;
  // Optional for a catalog operator: the API stores Paelode's code (0202).
  customerAbbreviation?: string;
  billingContactName?: string;
  billingContactEmail?: string;
  billingContactPhone?: string;
  billingAddress?: string;
  isPlatformOperator: boolean;
  // DEC-198 per-customer pricing (omit = no customer-level rate).
  perBblPriceCents?: number | null;
  hourlyPriceCents?: number | null;
  bidPriceCents?: number | null;
  // Migration 0086 — omit = no customer clock policy (inherit).
  billingStart?: 'ARRIVAL' | 'START' | null;
  billingEnd?: 'DEPARTURE' | 'COMPLETION' | null;
}

export function useCreateHaulerOperator(
  onDone: () => void,
): ReturnType<typeof useApiMutation<CreateHaulerOperatorBody, { id: string }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<CreateHaulerOperatorBody, { id: string }>(
    opts,
    '/v1/hauler-operators',
    'POST',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: HAULER_OPERATORS_KEY });
        onDone();
      },
    },
  );
}

export interface UpdateHaulerOperatorBody {
  customerAbbreviation?: string;
  billingContactName?: string | null;
  billingContactEmail?: string | null;
  billingContactPhone?: string | null;
  billingAddress?: string | null;
  // Task 24.6 — is-active toggle (DISPATCH_MANAGER+).
  isActive?: boolean;
  // DEC-198 per-customer pricing; null clears the rate (falls back to the
  // operator/job-type default), which is NOT the same as setting it to zero.
  perBblPriceCents?: number | null;
  hourlyPriceCents?: number | null;
  bidPriceCents?: number | null;
  // Migration 0086 — null clears the customer policy (falls back to operator/tenant).
  billingStart?: 'ARRIVAL' | 'START' | null;
  billingEnd?: 'DEPARTURE' | 'COMPLETION' | null;
}

export function useUpdateHaulerOperator(
  id: string,
): ReturnType<typeof useApiMutation<UpdateHaulerOperatorBody, { id: string }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<UpdateHaulerOperatorBody, { id: string }>(
    opts,
    `/v1/hauler-operators/${id}`,
    'PATCH',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: HAULER_OPERATORS_KEY });
      },
    },
  );
}

export function useDeleteHaulerOperator(
  id: string,
): ReturnType<typeof useApiMutation<void, { ok: boolean }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<void, { ok: boolean }>(opts, `/v1/hauler-operators/${id}`, 'DELETE', {
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: HAULER_OPERATORS_KEY });
    },
  });
}

// ─── Catalog search (platform operators + wells) ─────────────────────────────

export function usePlatformOperatorSearch(
  q: string,
): ReturnType<typeof useApiQuery<{ operators: readonly PlatformOperatorResult[] }>> {
  const opts = useApiOpts();
  const trimmed = q.trim();
  return useApiQuery<{ operators: readonly PlatformOperatorResult[] }>(
    opts,
    `/v1/platform-operators/search?q=${encodeURIComponent(trimmed)}`,
    {
      queryKey: ['platform-operators', 'search', trimmed],
      enabled: trimmed.length >= 2,
      staleTime: 30_000,
    },
  );
}

export function useWellSearch(
  q: string,
  operatorId?: string,
  // DEC-108 — "My Catalog": restrict to wells already in this tenant's catalog.
  // In that mode a query is optional (the hauler's own wells are bounded), so the
  // query is enabled even with no search term and a higher limit lists them all.
  onlyMyCatalog = false,
): ReturnType<typeof useApiQuery<{ wells: readonly WellResult[] }>> {
  const opts = useApiOpts();
  const trimmed = q.trim();
  const opParam = operatorId !== undefined ? `&operatorId=${encodeURIComponent(operatorId)}` : '';
  // The cap is named in shared so callers can tell a full page from a complete answer
  // (OPEN-053): a result of exactly WELL_SEARCH_MAX_LIMIT is probably truncated.
  const myParam = onlyMyCatalog ? `&onlyMyCatalog=true&limit=${String(WELL_SEARCH_MAX_LIMIT)}` : '';
  return useApiQuery<{ wells: readonly WellResult[] }>(
    opts,
    `/v1/wells/search?q=${encodeURIComponent(trimmed)}${opParam}${myParam}`,
    {
      queryKey: ['wells', 'search', trimmed, operatorId ?? '', onlyMyCatalog],
      enabled: onlyMyCatalog || trimmed.length >= 2,
      staleTime: 30_000,
    },
  );
}

// ─── Wells tab (detail + standalone well tanks + bulk import) — Task 24.11 ──────

export interface WellDetail {
  readonly id: string;
  readonly wellName: string;
  readonly apiNumber: string;
  readonly operatorId: string | null;
  readonly operatorName: string | null;
  readonly county: string | null;
  readonly state: string;
  readonly wellType: string | null;
  readonly wellheadLat: string | null;
  readonly wellheadLng: string | null;
  readonly isActive: boolean;
  // Non-null when this well is already linked to a tank battery for THIS tenant —
  // drives the conflict banner + hides the standalone "Add Tank" path.
  readonly tankBattery: { id: string; name: string } | null;
  // Task 24.11 (DEC-105) — the well is in this hauler's catalog (hauler_wells).
  readonly inHaulerCatalog: boolean;
}

export interface WellTank {
  readonly id: string;
  readonly name: string;
  readonly capacityBbl: string | null;
  readonly bblPerInch: string | null;
  readonly tankType: string | null;
  readonly producerIdentifier: string | null;
  readonly standardTankCatalogId: string | null;
  /** Phase 13.4a (0236) — the haul threshold (% full); null = none. Older APIs omit it. */
  readonly thresholdPercent?: string | null;
  readonly leadTimeHours?: number;
  readonly autoHaulJobTypeFamilyId?: string | null;
  readonly isActive: boolean;
  readonly createdAt: string;
  readonly createdByName: string | null;
}

export interface OperatorWell {
  readonly id: string;
  readonly wellName: string;
  readonly apiNumber: string;
  readonly county: string | null;
  readonly state: string;
  readonly wellType: string | null;
  // DEC-105 — "Already added" (hauler_wells, with or without tanks).
  readonly hasHaulerWells: boolean;
  // "Has tanks" (active standalone hauler_tanks).
  readonly hasHaulerTanks: boolean;
  readonly inTankBattery: boolean;
}

export interface BulkImportTank {
  name?: string;
  capacityBbl?: number;
  bblPerInch?: number;
  tankType?: string;
  standardTankCatalogId?: string;
}

export interface BulkImportBody {
  wellIds: string[];
  tank?: BulkImportTank;
}

export interface BulkImportResult {
  readonly imported: number;
  readonly skipped: number;
  readonly skippedReasons: readonly string[];
}

export const WELL_KEY = (id: string): readonly string[] => ['wells', id];
export const WELL_TANKS_KEY = (id: string): readonly string[] => ['wells', id, 'tanks'];
export const WELL_AUDIT_KEY = (id: string): readonly string[] => ['wells', id, 'audit'];

export function useWellDetail(id: string): ReturnType<typeof useApiQuery<{ well: WellDetail }>> {
  const opts = useApiOpts();
  return useApiQuery<{ well: WellDetail }>(opts, `/v1/wells/${id}`, {
    queryKey: WELL_KEY(id || '__none__'),
    enabled: id.length > 0,
    staleTime: 15_000,
  });
}

/** GET /v1/wells/:id/tanks — 13.4a adds the hauler's auto-haul switch for the well (null: no row). */
export interface WellTanksResponse {
  readonly tanks: readonly WellTank[];
  readonly autoHaulEnabled?: boolean | null;
}

export function useWellTanks(id: string): ReturnType<typeof useApiQuery<WellTanksResponse>> {
  const opts = useApiOpts();
  return useApiQuery<WellTanksResponse>(opts, `/v1/wells/${id}/tanks`, {
    queryKey: WELL_TANKS_KEY(id || '__none__'),
    enabled: id.length > 0,
    staleTime: 15_000,
  });
}

/** Phase 13.4a — PATCH /v1/wells/:id/auto-haul: the switch for this well's standalone tanks. */
export function useSetWellAutoHaul(
  wellId: string,
): ReturnType<
  typeof useApiMutation<{ enabled: boolean }, { wellId: string; autoHaulEnabled: boolean }>
> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<{ enabled: boolean }, { wellId: string; autoHaulEnabled: boolean }>(
    opts,
    `/v1/wells/${wellId}/auto-haul`,
    'PATCH',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: WELL_TANKS_KEY(wellId) });
      },
    },
  );
}

export function useWellAudit(
  id: string,
): ReturnType<typeof useApiQuery<{ audit: readonly AuditEntry[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ audit: readonly AuditEntry[] }>(opts, `/v1/wells/${id}/audit`, {
    queryKey: WELL_AUDIT_KEY(id || '__none__'),
    enabled: id.length > 0,
    staleTime: 15_000,
  });
}

export function useAddWellTank(
  wellId: string,
  onDone: () => void,
): ReturnType<typeof useApiMutation<AddTankBody, { id: string }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<AddTankBody, { id: string }>(opts, `/v1/wells/${wellId}/tanks`, 'POST', {
    onSuccess: () => {
      // Invalidate the whole 'wells' tree (search badges + detail + tanks + audit).
      void qc.invalidateQueries({ queryKey: ['wells'] });
      onDone();
    },
  });
}

// Edit / deactivate a standalone well tank — same /v1/tanks/:id endpoints as the
// battery tanks, but invalidate the 'wells' tree (not the battery keys).
export function useUpdateWellTank(
  tankId: string,
): ReturnType<typeof useApiMutation<UpdateTankBody, { id: string }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<UpdateTankBody, { id: string }>(opts, `/v1/tanks/${tankId}`, 'PATCH', {
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['wells'] });
    },
  });
}

export function useDeactivateWellTank(
  tankId: string,
): ReturnType<typeof useApiMutation<void, { ok: boolean }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<void, { ok: boolean }>(opts, `/v1/tanks/${tankId}`, 'DELETE', {
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['wells'] });
    },
  });
}

export function useOperatorWells(
  operatorId: string,
  state?: string,
  county?: string,
  // DEC-108 — restrict to wells already in this tenant's catalog ("My Catalog").
  onlyMyCatalog = false,
): ReturnType<typeof useApiQuery<{ wells: readonly OperatorWell[] }>> {
  const opts = useApiOpts();
  const params = new URLSearchParams();
  if (state !== undefined && state !== '') params.set('state', state);
  if (county !== undefined && county !== '') params.set('county', county);
  if (onlyMyCatalog) params.set('onlyMyCatalog', 'true');
  const qs = params.toString();
  return useApiQuery<{ wells: readonly OperatorWell[] }>(
    opts,
    `/v1/platform-operators/${operatorId}/wells${qs === '' ? '' : `?${qs}`}`,
    {
      queryKey: ['operator-wells', operatorId, state ?? '', county ?? '', onlyMyCatalog],
      enabled: operatorId.length > 0,
      staleTime: 15_000,
    },
  );
}

export function useBulkImportWells(
  onDone: (result: BulkImportResult) => void,
): ReturnType<typeof useApiMutation<BulkImportBody, BulkImportResult>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<BulkImportBody, BulkImportResult>(opts, '/v1/wells/bulk-import', 'POST', {
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ['wells'] });
      void qc.invalidateQueries({ queryKey: ['operator-wells'] });
      onDone(result);
    },
  });
}

// ─── Tank batteries ──────────────────────────────────────────────────────────

export function useTankBatteries(): ReturnType<
  typeof useApiQuery<{ tankBatteries: readonly TankBatterySummary[] }>
> {
  const opts = useApiOpts();
  return useApiQuery<{ tankBatteries: readonly TankBatterySummary[] }>(opts, '/v1/tank-batteries', {
    queryKey: TANK_BATTERIES_KEY,
    staleTime: 30_000,
  });
}

export function useTankBattery(
  id: string,
): ReturnType<typeof useApiQuery<{ tankBattery: TankBatteryDetail }>> {
  const opts = useApiOpts();
  return useApiQuery<{ tankBattery: TankBatteryDetail }>(opts, `/v1/tank-batteries/${id}`, {
    queryKey: TANK_BATTERY_KEY(id || '__none__'),
    enabled: id.length > 0,
    staleTime: 15_000,
  });
}

export function useTankBatteryAudit(
  id: string,
): ReturnType<typeof useApiQuery<{ audit: readonly AuditEntry[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ audit: readonly AuditEntry[] }>(opts, `/v1/tank-batteries/${id}/audit`, {
    queryKey: TANK_BATTERY_AUDIT_KEY(id || '__none__'),
    enabled: id.length > 0,
    staleTime: 15_000,
  });
}

// Phase 26 / Task 26.5 (DEC-147 #5/#10) — Create Location body. `siteTypeId`
// is the tenant-filtered site_types.id chosen by the picker (default from the
// UI is TANK_BATTERY). `canPickup` / `canDropoff` are the capability toggles;
// when omitted the server keeps the schema defaults (pickup=true, dropoff=false).
export interface CreateTankBatteryBody {
  name: string;
  haulerOperatorId: string;
  isPrivate: boolean;
  siteTypeId?: string;
  canPickup?: boolean;
  canDropoff?: boolean;
  // DEC-310 — a typed county (the server uppercases it); countyState is required with it.
  county?: string;
  countyState?: string;
}

export function useCreateTankBattery(
  onDone: () => void,
): ReturnType<typeof useApiMutation<CreateTankBatteryBody, { id: string }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<CreateTankBatteryBody, { id: string }>(opts, '/v1/tank-batteries', 'POST', {
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TANK_BATTERIES_KEY });
      onDone();
    },
  });
}

// Phase 26 / Task 26.5 (DEC-147 #10) — patch body for the location detail
// screen. Rename is still the common case; site type + capability toggles are
// the new affordances. The server enforces "at least one field" (Zod refine).
export interface UpdateTankBatteryBody {
  name?: string;
  siteTypeId?: string;
  canPickup?: boolean;
  canDropoff?: boolean;
  // DEC-310 — a string types the county (source USER); null clears it back to the GPS value.
  county?: string | null;
  countyState?: string;
  /** Phase 13.4a — the per-site auto-haul switch (needs the AUTO_HAUL plan feature). */
  autoHaulEnabled?: boolean;
}

export function useRenameTankBattery(
  id: string,
): ReturnType<typeof useApiMutation<UpdateTankBatteryBody, { id: string }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<UpdateTankBatteryBody, { id: string }>(
    opts,
    `/v1/tank-batteries/${id}`,
    'PATCH',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: TANK_BATTERIES_KEY });
        void qc.invalidateQueries({ queryKey: TANK_BATTERY_KEY(id) });
      },
    },
  );
}

export interface LinkWellBody {
  wellId: string;
  // Task 24.6 — required to proceed when the well has an active standalone tank.
  deactivateStandaloneTank?: boolean;
}

export function useLinkWell(
  batteryId: string,
): ReturnType<typeof useApiMutation<LinkWellBody, { wellId: string }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<LinkWellBody, { wellId: string }>(
    opts,
    `/v1/tank-batteries/${batteryId}/wells`,
    'POST',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: TANK_BATTERY_KEY(batteryId) });
        void qc.invalidateQueries({ queryKey: TANK_BATTERY_AUDIT_KEY(batteryId) });
        void qc.invalidateQueries({ queryKey: TANK_BATTERIES_KEY });
      },
    },
  );
}

export function useUnlinkWell(
  batteryId: string,
  wellId: string,
): ReturnType<typeof useApiMutation<void, { ok: boolean }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<void, { ok: boolean }>(
    opts,
    `/v1/tank-batteries/${batteryId}/wells/${wellId}`,
    'DELETE',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: TANK_BATTERY_KEY(batteryId) });
        void qc.invalidateQueries({ queryKey: TANK_BATTERY_AUDIT_KEY(batteryId) });
        void qc.invalidateQueries({ queryKey: TANK_BATTERIES_KEY });
      },
    },
  );
}

export interface AddTankBody {
  name: string;
  capacityBbl?: number;
  bblPerInch?: number;
  tankType?: string;
  producerIdentifier?: string;
  standardTankCatalogId?: string;
}

export function useAddTank(
  batteryId: string,
  onDone: () => void,
): ReturnType<typeof useApiMutation<AddTankBody, { id: string }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<AddTankBody, { id: string }>(
    opts,
    `/v1/tank-batteries/${batteryId}/tanks`,
    'POST',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: TANK_BATTERY_KEY(batteryId) });
        void qc.invalidateQueries({ queryKey: TANK_BATTERY_AUDIT_KEY(batteryId) });
        void qc.invalidateQueries({ queryKey: TANK_BATTERIES_KEY });
        onDone();
      },
    },
  );
}

export interface UpdateTankBody {
  name?: string;
  capacityBbl?: number | null;
  bblPerInch?: number | null;
  tankType?: string | null;
  producerIdentifier?: string | null;
  // Phase 13.4a (0236) — the haul threshold.
  thresholdPercent?: number | null;
  leadTimeHours?: number;
  autoHaulJobTypeFamilyId?: string | null;
}

export function useUpdateTank(
  tankId: string,
  batteryId: string,
): ReturnType<typeof useApiMutation<UpdateTankBody, { id: string }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<UpdateTankBody, { id: string }>(opts, `/v1/tanks/${tankId}`, 'PATCH', {
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TANK_BATTERY_KEY(batteryId) });
      void qc.invalidateQueries({ queryKey: TANK_BATTERY_AUDIT_KEY(batteryId) });
    },
  });
}

export function useDeactivateTank(
  tankId: string,
  batteryId: string,
): ReturnType<typeof useApiMutation<void, { ok: boolean }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<void, { ok: boolean }>(opts, `/v1/tanks/${tankId}`, 'DELETE', {
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TANK_BATTERY_KEY(batteryId) });
      void qc.invalidateQueries({ queryKey: TANK_BATTERY_AUDIT_KEY(batteryId) });
      void qc.invalidateQueries({ queryKey: TANK_BATTERIES_KEY });
    },
  });
}

export function useStandardTanks(): ReturnType<
  typeof useApiQuery<{ standardTanks: readonly StandardTank[] }>
> {
  const opts = useApiOpts();
  return useApiQuery<{ standardTanks: readonly StandardTank[] }>(
    opts,
    '/v1/platform/tank-catalog',
    {
      queryKey: STANDARD_TANKS_KEY,
      staleTime: 300_000,
    },
  );
}

// ─── Disposal facilities ─────────────────────────────────────────────────────

export function useDisposalFacilities(): ReturnType<
  typeof useApiQuery<{ haulerDisposalFacilities: readonly DisposalFacility[] }>
> {
  const opts = useApiOpts();
  return useApiQuery<{ haulerDisposalFacilities: readonly DisposalFacility[] }>(
    opts,
    '/v1/hauler-disposal-facilities',
    { queryKey: DISPOSAL_FACILITIES_KEY, staleTime: 30_000 },
  );
}

export function useAuthorizeDisposal(
  onDone: () => void,
): ReturnType<typeof useApiMutation<{ wellId: string; displayName?: string }, { id: string }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<{ wellId: string; displayName?: string }, { id: string }>(
    opts,
    '/v1/hauler-disposal-facilities',
    'POST',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: DISPOSAL_FACILITIES_KEY });
        onDone();
      },
    },
  );
}

export function useUpdateDisposal(
  id: string,
): ReturnType<typeof useApiMutation<{ displayName: string | null }, { id: string }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<{ displayName: string | null }, { id: string }>(
    opts,
    `/v1/hauler-disposal-facilities/${id}`,
    'PATCH',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: DISPOSAL_FACILITIES_KEY });
      },
    },
  );
}

export function useDeactivateDisposal(
  id: string,
): ReturnType<typeof useApiMutation<void, { ok: boolean }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<void, { ok: boolean }>(
    opts,
    `/v1/hauler-disposal-facilities/${id}`,
    'DELETE',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: DISPOSAL_FACILITIES_KEY });
      },
    },
  );
}

// ─── Shared display helpers ──────────────────────────────────────────────────

const ROLE_LABELS: Readonly<Record<string, string>> = {
  PLATFORM_ADMIN: 'Platform Admin',
  TENANT_ADMIN: 'Tenant Admin',
  DISPATCH_MANAGER: 'Dispatch Manager',
  DISPATCHER: 'Dispatcher',
  TRUCK_PUSHER: 'Truck Pusher',
  BILLING_MANAGER: 'Billing Manager',
  BILLING_STAFF: 'Billing Staff',
  DRIVER: 'Driver',
};

/** Human label for a role code (audit trail), e.g. DISPATCH_MANAGER → "Dispatch Manager". */
export function roleLabel(code: string | null): string {
  if (code === null) return '';
  return ROLE_LABELS[code] ?? code;
}
